import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, LayoutGridIcon, UserRoundIcon, IdCardIcon, MonitorSmartphoneIcon, MessageSquareTextIcon, FileTextIcon, DatabaseIcon, ShieldCheckIcon, CalendarDaysIcon, PawPrintIcon, DropletIcon, SparklesIcon } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import type { Bi } from '../data/sectors';
import { platformItems } from '../data/navigation';
import { exploreCopy as c, exploreGroups, type ExploreGroup, type ExploreTile } from '../data/platformExplore';
const ICONS: Record<string, typeof LayoutGridIcon> = {
  overview: LayoutGridIcon,
  identity: UserRoundIcon,
  'digital-card': IdCardIcon,
  'digital-experience': MonitorSmartphoneIcon,
  communications: MessageSquareTextIcon,
  requests: FileTextIcon,
  collect: DatabaseIcon,
  permits: ShieldCheckIcon,
  recreation: CalendarDaysIcon,
  'pet-licensing': PawPrintIcon,
  'smart-water': DropletIcon,
  atna: SparklesIcon
};
const routeFor = (navId: string) => platformItems.find(i => i.id === navId)?.to ?? '/platform';
const GROUP_COLS: Record<ExploreGroup['id'], string> = {
  foundation: 'grid-cols-2 sm:grid-cols-4',
  services: 'grid-cols-2 sm:grid-cols-4 lg:grid-cols-7',
  intelligence: 'grid-cols-2 sm:grid-cols-4 lg:grid-cols-1'
};
function Tile({
  tile,
  pick,
  accent
}: {
  tile: ExploreTile;
  pick: (b: Bi) => string;
  accent: boolean;
}) {
  const Icon = ICONS[tile.navId] ?? LayoutGridIcon;
  return <Link to={routeFor(tile.navId)} className={`group flex h-full flex-col items-center justify-between gap-3 rounded-xl border px-2 pt-4 pb-3 text-center cursor-pointer transition-[background-color,border-color,box-shadow] duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5] focus-visible:ring-offset-2 ${accent ? 'border-[#DCD9FA] bg-[#F4F3FE] hover:border-[#A5A0F3] hover:bg-[#EEEDFD]' : 'border-[#ECEDF2] bg-white hover:border-[#C7C3F6] hover:bg-[#F8F7FF] hover:shadow-[0_8px_20px_-16px_rgba(79,70,229,0.5)]'}`} data-id="element-5451" data-mp="165zw2ljr22je">
      <Icon className="w-[22px] h-[22px] text-[#4F46E5]" strokeWidth={1.6} aria-hidden="true" data-id="element-5452" data-mp="3g0syg15dh18t" />
      <span className="font-sans text-[13px] font-medium leading-tight text-[#121A3A] min-h-[2.1em] flex items-center" data-id="element-5453" data-mp="3up5ji32x8zoj">{pick(tile.label)}</span>
      <span className={`w-7 h-7 rounded-full flex items-center justify-center transition-transform duration-150 ease-out group-hover:translate-x-1 ${accent ? 'bg-[#4F46E5] text-white' : 'border border-[#E4E3F2] text-[#4F46E5] group-hover:border-[#C7C3F6]'}`} aria-hidden="true" data-id="element-5454" data-mp="3pxxbnbh8pmdh">
        <ArrowRightIcon className="w-3.5 h-3.5" strokeWidth={2} data-id="element-5455" data-mp="hgt1hpn6gjj1" />
      </span>
    </Link>;
}
export function PlatformExplore() {
  const {
    lang
  } = useLanguage();
  const pick = (b: Bi) => lang === 'FR' ? b.fr : b.en;
  return <section className="relative bg-[#FAFAFD] border-t border-b border-[#ECEDF2] px-6 py-14 lg:py-16" aria-labelledby="platform-explore-title" data-id="element-5456" data-mp="2ebfcangi9vao">
      <div className="max-w-7xl mx-auto" data-id="element-5457" data-mp="tug176vhnz5w">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4" data-id="element-5458" data-mp="ys80ilygqnk4">
          <div data-id="element-5459" data-mp="yu2aj5874v9f">
            <p className="font-sans text-xs font-semibold tracking-[0.22em] uppercase text-[#4F46E5]" data-id="element-5460" data-mp="k3la9p7asbe4">{pick(c.eyebrow)}</p>
            <h2 id="platform-explore-title" className="mt-3 font-serif text-[1.8rem] sm:text-[2.1rem] lg:text-[2.3rem] leading-[1.1] tracking-tight text-[#121A3A]" data-id="element-5461" data-mp="p1mtca4q4apl">
              {c.headline.map((l, i) => <span key={l.fr} className="inline" data-id="element-5462" data-mp="g2rqn80w1ir1" data-mpi={i}>
                  {i > 0 && ' '}
                  {pick(l)}
                </span>)}
            </h2>
          </div>
          <Link to={routeFor('overview')} className="group inline-flex items-center gap-2 self-start md:self-auto font-sans text-sm font-medium text-[#4F46E5] hover:text-[#4338CA] transition-colors duration-150 whitespace-nowrap" data-id="element-5463" data-mp="62r1h5gc88ex">
            {pick(c.link)}
            <ArrowRightIcon className="w-4 h-4 transition-transform duration-150 ease-out group-hover:translate-x-1" aria-hidden="true" data-id="element-5464" data-mp="ftzyj52wuv3" />
          </Link>
        </div>

        <div className="mt-9 grid grid-cols-1 lg:grid-cols-[4fr_7fr_1.15fr] gap-4 lg:gap-5" data-id="element-5465" data-mp="3sha65msx6n0a">
          {exploreGroups.map(g => <div key={g.id} className="rounded-2xl border border-[#ECEDF2] bg-white p-3.5" data-id="element-5466" data-mp="3ilqb12m6jscl">
              <p className="flex items-center gap-2 px-1 font-sans text-[11px] font-semibold tracking-[0.2em] uppercase text-[#121A3A]" data-id="element-5467" data-mp="3dmdqwpmp0zdp">
                <span className="w-2 h-2 bg-[#4F46E5]" aria-hidden="true" data-id="element-5468" data-mp="38tkls0nbbp13" />
                {pick(g.label)}
              </p>
              <ul className={`mt-3 grid gap-2 ${GROUP_COLS[g.id]}`} data-id="element-5469" data-mp="34r61dc3zwh7p">
                {g.tiles.map(t => <li key={t.navId} data-id="element-5470" data-mp="2uvcr5zdr0zt3">
                    <Tile tile={t} pick={pick} accent={g.id === 'intelligence'} data-id="element-5471" data-mp="2uub38ubmwity" />
                  </li>)}
              </ul>
            </div>)}
        </div>
      </div>
    </section>;
}